import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import moment from 'moment';
import validator from 'validator';
import {
  Typography,
  Button,
  Input,
  InputContainer,
  DatePicker,
  BelongsToSelects,
  SearchableSelect,
} from '../../design-system';
import { DashboardHeader, DashboardForm, DashboardFormCustomColumn } from '../dashboard';
import { useStore } from '../../context';
import { useGateOutContainerMutation } from '../../hooks';
import { showToRoles, getNormalFormProps, momentMaxDate } from '../../utils';
import { User } from '../../types';
import { containersService } from '../../services';
import { INPUT_ERRORS, REGEX } from '../../const';

let formProps: any = {};

const getSubmitProps = (user: User) => {
  const props: any = {
    containerNumber: formProps.containerNumber.length,
    clientId: formProps.clientId,
    yardId: formProps.yardId,
    licensePlateNumber: formProps.licensePlateNumber,
  };

  const errors: any = {
    completedAt:
      !showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_MANAGER'],
        user,
      ) ||
      (moment(formProps.completedAt).set('hour', 0).isBefore(moment().set('hour', 1)) &&
        moment(formProps.completedAt)
          .set('hour', 23)
          .isAfter(moment(formProps.minDate).set('hour', 0))),
    hasCompletedAt:
      !showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_MANAGER'],
        user,
      ) || !!formProps.completedAt,
  };

  if (
    [
      ...Object.values(props),
      ...Object.values(errors),
      ...Object.values(formProps.validators)
        .flat()
        .map((val) => !val),
    ].some((val) => !val)
  ) {
    return null;
  }

  delete formProps.minDate;
  delete formProps.isSubmitClicked;
  if (
    !showToRoles(
      ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_MANAGER'],
      user,
    )
  )
    delete formProps.completedAt;
  if (
    showToRoles(
      ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_MANAGER'],
      user,
    )
  )
    formProps.completedAt = moment(formProps.completedAt).set({
      hour: 23,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

  return getNormalFormProps(formProps);
};

export const ContainersOutHeader = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();
  const { mutate } = useGateOutContainerMutation();

  const handleSubmitClick = () => {
    formProps.onSubmitClick();
    const props = getSubmitProps(user);
    if (!props) return;

    mutate(props, {
      onSuccess: () => {
        navigate('/dashboard/containers');
      },
    });
  };

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        Gate Out Container
      </Typography>
      <Button size={'medium'} data-color={'white'} to={-1}>
        Go Back
      </Button>
      <Button size={'medium'} onClick={handleSubmitClick}>
        Submit
      </Button>
    </DashboardHeader>
  );
};

export const ContainersOutForm = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const [yardName, setYardName] = useState('');
  const yardId = useRef('');
  const [clientName, setClientName] = useState('');
  const clientId = useRef('');
  const [completedAt, setCompletedAt] = useState();

  const [containerNumber, setContainerNumber] = useState<any[]>(['']);
  const gateInDate = useRef<any[]>([null]);
  const [licensePlateNumber, setLicensePlateNumber] = useState(['']);
  const [driverFirstName, setDriverFirstName] = useState(['']);
  const [driverLastName, setDriverLastName] = useState(['']);

  const [updateKey, setUpdate] = useState<any>();

  const validators = {
    driverFirstName: driverFirstName.map(
      (value) =>
        !!value && (!validator.isLength(value, { max: 30 }) || !REGEX.USER_NAME.test(value)),
    ),
    driverLastName: driverLastName.map(
      (value) =>
        !!value && (!validator.isLength(value, { max: 30 }) || !REGEX.USER_NAME.test(value)),
    ),
    licensePlateNumber: licensePlateNumber.map((value) =>
      value
        ? !validator.isLength(value, { min: 4, max: 10 })
          ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER_LENGTH
          : !REGEX.LICENSE_PLATE_NUMBER.test(value)
          ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER
          : ''
        : formProps.isSubmitClicked
        ? INPUT_ERRORS.REQUIRED.LICENSE_PLATE_NUMBER
        : '',
    ),
  };

  useEffect(() => {
    if (!formProps) return;

    formProps.isSubmitClicked = false;
    setUpdate(Math.random());
  }, []);

  useEffect(() => {
    if (!yardName || !clientName) return;

    setContainerNumber(['']);
    gateInDate.current = [null];
    setLicensePlateNumber(['']);
    setDriverFirstName(['']);
    setDriverLastName(['']);
  }, [yardName, clientName]);

  useEffect(() => {
    formProps = {
      ...formProps,
      yardId: yardId.current,
      clientId: clientId.current,
      containerNumber,
      minDate: momentMaxDate(gateInDate.current),
      licensePlateNumber,
      completedAt,
      driverFirstName,
      driverLastName,
      validators,
      onSubmitClick: () => {
        formProps.isSubmitClicked = true;
        setUpdate(Math.random());
      },
    };
  }, [
    yardId.current,
    clientId.current,
    completedAt,
    containerNumber.join(''),
    containerNumber.length,
    licensePlateNumber.join(),
    driverFirstName.join(),
    driverLastName.join(),
  ]);

  const handleContainerSelect = (option: any, i: number) => {
    const newContainerNumber = containerNumber;

    newContainerNumber[i] = option.value;

    gateInDate.current[i] = option.gateInDate;
    setContainerNumber([...newContainerNumber]);

    return;
  };

  const handlePlusClick = () => {
    setContainerNumber([...containerNumber, '']);
    gateInDate.current = [...gateInDate.current, null];
    setLicensePlateNumber([...licensePlateNumber, '']);
    setDriverFirstName([...driverFirstName, '']);
    setDriverLastName([...driverLastName, '']);
  };

  const handleDeleteClick = (i: number) => {
    setUpdate(Math.random());

    const filteredContainerNumber = containerNumber.filter((_: any, index: any) => i !== index);
    const filteredGateInDate = gateInDate.current.filter((_: any, index: any) => i !== index);
    const filteredLicensePlateNumber = licensePlateNumber.filter(
      (_: any, index: any) => i !== index,
    );
    const filteredDriverFirstName = driverFirstName.filter((_: any, index: any) => i !== index);
    const filteredDriverLastName = driverLastName.filter((_: any, index: any) => i !== index);

    setContainerNumber(filteredContainerNumber);
    gateInDate.current = filteredGateInDate;
    setLicensePlateNumber(filteredLicensePlateNumber);
    setDriverFirstName(filteredDriverFirstName);
    setDriverLastName(filteredDriverLastName);
  };

  return (
    <DashboardForm paperSize={'full-size'}>
      <BelongsToSelects
        yardId={yardId.current}
        yardName={yardName}
        clientId={clientId.current}
        clientName={clientName}
        handleYardChange={(obj) => {
          yardId.current = obj.id;
          setYardName(obj.label);
        }}
        handleClientChange={(obj) => {
          clientId.current = obj.id;
          setClientName(obj.label);
        }}
        isSubmitClicked={formProps.isSubmitClicked}
      />
      {containerNumber.map((val: any, i: any) => (
        <DashboardFormCustomColumn
          key={updateKey + i}
          columns={
            containerNumber.length === 1 || i !== 0
              ? 'calc(100%/4 - 2.8rem*(3/4)) calc(100%/4 - 2.8rem*(3/4)) calc(100%/4 - 2.8rem*(3/4)) 1fr max-content'
              : '1fr 1fr 1fr 1fr'
          }
          align={'start'}
        >
          <SearchableSelect
            InputComponent={InputContainer}
            id={`container-${i}`}
            label={`Container Number*`}
            selectedOptionLabel={containerNumber[i] || 'Select Container Number'}
            onSelect={(option) => handleContainerSelect(option, i)}
            queryKey={[
              'container-short',
              'disabled containers' + containerNumber.join(''),
              showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'], user)
                ? yardName && clientName
                  ? `Container Number${yardName + clientName}`
                  : 'Container Number'
                : `Container Number${yardName + clientName}`,
            ]}
            queryFn={() =>
              containersService.options(
                {
                  yardId: yardId.current,
                  clientId: clientId.current,
                  status: 'GATED_IN',
                  short: true,
                  disabledNumbers: containerNumber,
                },
                user,
              )
            }
            error={formProps.isSubmitClicked && !containerNumber[i]}
            helperText={
              formProps.isSubmitClicked &&
              !containerNumber[i] &&
              INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER
            }
          />
          <Input
            id={`license-plate-number-${i}`}
            label={`License Plate Number*`}
            placeholder={'10Z234EB'}
            data-size={'medium'}
            defaultValue={licensePlateNumber[i]}
            onChange={(e) => {
              const newLicensePlateNumber = licensePlateNumber;
              newLicensePlateNumber[i] = e.target.value;
              setLicensePlateNumber([...newLicensePlateNumber]);
            }}
            error={!!validators.licensePlateNumber[i]}
            helperText={validators.licensePlateNumber[i]}
          />
          <Input
            id={`driver-first-name-${i}`}
            label={`Driver First Name`}
            placeholder={'Driver First Name'}
            data-size={'medium'}
            defaultValue={driverFirstName[i]}
            onChange={(e) => {
              const newDriverFirstName = driverFirstName;
              newDriverFirstName[i] = e.target.value;
              setDriverFirstName([...newDriverFirstName]);
            }}
            error={validators.driverFirstName[i]}
            helperText={validators.driverFirstName[i] ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME : ''}
          />
          <Input
            id={`driver-last-name-${i}`}
            label={`Driver Last Name`}
            placeholder={'Driver Last Name'}
            data-size={'medium'}
            defaultValue={driverLastName[i]}
            onChange={(e) => {
              const newDriverLastName = driverLastName;
              newDriverLastName[i] = e.target.value;
              setDriverLastName([...newDriverLastName]);
            }}
            error={validators.driverLastName[i]}
            helperText={validators.driverLastName[i] ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME : ''}
          />
          {i !== 0 && (
            <Button
              className={'row-button-square'}
              size={'medium'}
              data-color={'destructiveSecondary'}
              isSquare={true}
              onClick={() => handleDeleteClick(i)}
            >
              <DeleteIcon />
            </Button>
          )}
          {containerNumber.length === 1 && (
            <Button
              className={'row-button-square'}
              size={'medium'}
              data-color={'secondary'}
              isSquare={true}
              onClick={handlePlusClick}
            >
              <AddIcon />
            </Button>
          )}
        </DashboardFormCustomColumn>
      ))}
      {containerNumber.length > 1 && (
        <Button size={'medium'} data-color={'secondary'} onClick={handlePlusClick} justify={'left'}>
          Add More
        </Button>
      )}
      {showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE', 'YARD_MANAGER'],
        user,
      ) && (
        <DatePicker
          label={'Gated Out Date*'}
          data-size={'medium'}
          defaultValue={completedAt}
          onChange={setCompletedAt}
          maxDate={moment()}
          minDate={momentMaxDate(gateInDate.current)}
          error={formProps.isSubmitClicked && !completedAt}
          helperText={formProps.isSubmitClicked && !completedAt && INPUT_ERRORS.REQUIRED.DATE}
        />
      )}
    </DashboardForm>
  );
};
